export type Dimensions = { x: number; y: number; z: number };
export * from "./economics";
export * from "./production-economics";
export * from "./machine-setup";
export * from "./material-workflows";
export * from "./print-readiness";
export * from "./fastener-fit";
export * from "./seller-handoff";
export * from "./print-diagnostics";

function finiteNonNegative(value: number, name: string): number {
  if (!Number.isFinite(value) || value < 0) throw new RangeError(`${name} must be a finite non-negative number.`);
  return value;
}

export function filamentLengthMeters(massGrams: number, diameterMm: number, densityGramsPerCm3: number): number {
  const mass = finiteNonNegative(massGrams, "mass");
  const diameter = finiteNonNegative(diameterMm, "diameter");
  const density = finiteNonNegative(densityGramsPerCm3, "density");
  if (diameter === 0 || density === 0) throw new RangeError("Diameter and density must be greater than zero.");
  const volumeMm3 = (mass / density) * 1000;
  const areaMm2 = Math.PI * (diameter / 2) ** 2;
  return volumeMm3 / areaMm2 / 1000;
}

export function filamentMassGrams(lengthMeters: number, diameterMm: number, densityGramsPerCm3: number): number {
  const length = finiteNonNegative(lengthMeters, "length");
  const diameter = finiteNonNegative(diameterMm, "diameter");
  const density = finiteNonNegative(densityGramsPerCm3, "density");
  if (diameter === 0 || density === 0) throw new RangeError("Diameter and density must be greater than zero.");
  const volumeCm3 = (length * 1000 * Math.PI * (diameter / 2) ** 2) / 1000;
  return volumeCm3 * density;
}

export function calculateSpoolSufficiency(grossSpoolGrams: number, emptySpoolGrams: number, jobGrams: number, reservePercent: number) {
  const gross = finiteNonNegative(grossSpoolGrams, "gross spool mass");
  const empty = finiteNonNegative(emptySpoolGrams, "empty spool mass");
  const job = finiteNonNegative(jobGrams, "job mass");
  const reserve = finiteNonNegative(reservePercent, "reserve percent");
  if (empty > gross) throw new RangeError("Empty spool mass cannot exceed gross spool mass.");
  if (job === 0) throw new RangeError("Job mass must be greater than zero.");
  if (reserve > 100) throw new RangeError("Reserve percent cannot exceed 100.");
  const netAvailable = gross - empty;
  const reserved = netAvailable * reserve / 100;
  const usable = Math.max(0, netAvailable - reserved);
  const surplus = usable - job;
  return { netAvailable, reserved, usable, job, enough: surplus >= 0, surplus: Math.max(0, surplus), shortfall: Math.max(0, -surplus), completeJobs: Math.floor(usable / job) };
}

export function volumetricFlowMm3s(lineWidthMm: number, layerHeightMm: number, speedMmS: number): number {
  return finiteNonNegative(lineWidthMm, "line width") * finiteNonNegative(layerHeightMm, "layer height") * finiteNonNegative(speedMmS, "speed");
}

export function speedForFlowMmS(flowMm3s: number, lineWidthMm: number, layerHeightMm: number): number {
  const area = finiteNonNegative(lineWidthMm, "line width") * finiteNonNegative(layerHeightMm, "layer height");
  if (area === 0) throw new RangeError("Line width and layer height must be greater than zero.");
  return finiteNonNegative(flowMm3s, "flow") / area;
}

export type PrintCostInput = {
  massGrams: number;
  materialPerKg: number;
  printHours: number;
  machinePerHour: number;
  averageWatts: number;
  electricityPerKwh: number;
  laborMinutes: number;
  laborPerHour: number;
  failurePercent: number;
  marginPercent: number;
};

export function calculatePrintCost(input: PrintCostInput) {
  const material = finiteNonNegative(input.massGrams, "mass") / 1000 * finiteNonNegative(input.materialPerKg, "material price");
  const machine = finiteNonNegative(input.printHours, "print hours") * finiteNonNegative(input.machinePerHour, "machine rate");
  const electricity = finiteNonNegative(input.averageWatts, "watts") / 1000 * input.printHours * finiteNonNegative(input.electricityPerKwh, "electricity rate");
  const labor = finiteNonNegative(input.laborMinutes, "labor minutes") / 60 * finiteNonNegative(input.laborPerHour, "labor rate");
  const failureRate = Math.min(finiteNonNegative(input.failurePercent, "failure percent") / 100, 0.95);
  const base = material + machine + electricity + labor;
  const withFailure = base / (1 - failureRate);
  const marginRate = Math.min(finiteNonNegative(input.marginPercent, "margin percent") / 100, 0.95);
  const price = withFailure / (1 - marginRate);
  return { material, machine, electricity, labor, failureAllowance: withFailure - base, cost: withFailure, price };
}

const permutations = (dimensions: Dimensions): Dimensions[] => [
  { x: dimensions.x, y: dimensions.y, z: dimensions.z },
  { x: dimensions.x, y: dimensions.z, z: dimensions.y },
  { x: dimensions.y, y: dimensions.x, z: dimensions.z },
  { x: dimensions.y, y: dimensions.z, z: dimensions.x },
  { x: dimensions.z, y: dimensions.x, z: dimensions.y },
  { x: dimensions.z, y: dimensions.y, z: dimensions.x }
];

export function findBuildVolumeFit(model: Dimensions, build: Dimensions): Dimensions | undefined {
  for (const value of [...Object.values(model), ...Object.values(build)]) finiteNonNegative(value, "dimension");
  return permutations(model).find((candidate) => candidate.x <= build.x && candidate.y <= build.y && candidate.z <= build.z);
}

export function calculateLayerStack(modelHeightMm: number, firstLayerMm: number, layerHeightMm: number) {
  const modelHeight = finiteNonNegative(modelHeightMm, "model height"), firstLayer = finiteNonNegative(firstLayerMm, "first layer"), layerHeight = finiteNonNegative(layerHeightMm, "layer height");
  if (modelHeight === 0 || firstLayer === 0 || layerHeight === 0) throw new RangeError("Model height, first layer, and layer height must be greater than zero.");
  const layers = modelHeight <= firstLayer ? 1 : 1 + Math.ceil((modelHeight - firstLayer - Number.EPSILON) / layerHeight), printedHeight = firstLayer + (layers - 1) * layerHeight;
  return { layers, printedHeight, heightDelta: printedHeight - modelHeight, regularLayers: layers - 1 };
}

export function calculateDimensionalCompensation(designedMm: number, measuredMm: number, targetMm: number) {
  const designed = finiteNonNegative(designedMm, "designed dimension"), measured = finiteNonNegative(measuredMm, "measured dimension"), target = finiteNonNegative(targetMm, "target dimension");
  if (designed === 0 || measured === 0 || target === 0) throw new RangeError("Designed, measured, and target dimensions must be greater than zero.");
  const scaleFactor = target / measured;
  return { scaleFactor, scalePercent: scaleFactor * 100, adjustedDesignMm: designed * scaleFactor, measuredErrorMm: measured - target, measuredErrorPercent: (measured - target) / target * 100 };
}

export type ResinCostInput = { partVolumeMl: number; supportPercent: number; vatLossMl: number; resinPerLiter: number; densityGramsPerMl: number; consumables: number; failurePercent: number };
export function calculateResinCost(input: ResinCostInput) {
  const partVolume = finiteNonNegative(input.partVolumeMl, "part volume"), supportPercent = finiteNonNegative(input.supportPercent, "support percent"), vatLoss = finiteNonNegative(input.vatLossMl, "vat loss"), resinPerLiter = finiteNonNegative(input.resinPerLiter, "resin price"), density = finiteNonNegative(input.densityGramsPerMl, "density"), consumables = finiteNonNegative(input.consumables, "consumables"), failurePercent = finiteNonNegative(input.failurePercent, "failure percent");
  if (density === 0) throw new RangeError("Density must be greater than zero.");
  if (failurePercent >= 100) throw new RangeError("Failure percent must be less than 100.");
  const supportVolumeMl = partVolume * supportPercent / 100, resinVolumeMl = partVolume + supportVolumeMl + vatLoss, resinMassGrams = resinVolumeMl * density, materialCost = resinVolumeMl / 1000 * resinPerLiter, baseCost = materialCost + consumables, expectedCost = baseCost / (1 - failurePercent / 100);
  return { supportVolumeMl, resinVolumeMl, resinMassGrams, materialCost, consumables, failureAllowance: expectedCost - baseCost, expectedCost };
}

export type MoldMixRatioBasis = "mass" | "volume";
export type MoldMixInput = {
  cavityVolumeMl: number;
  pourCount: number;
  transferReserveMl: number;
  wastePercent: number;
  ratioBasis: MoldMixRatioBasis;
  partARatio: number;
  partBRatio: number;
  partADensityGramsPerMl: number;
  partBDensityGramsPerMl: number;
  partAPricePerKg: number;
  partBPricePerKg: number;
  maximumBatchVolumeMl: number;
  linearShrinkPercent: number;
};

export type MoldMixResult = {
  targetCavityVolumeMl: number;
  workingVolumeMl: number;
  wasteVolumeMl: number;
  totalMixVolumeMl: number;
  partAVolumeMl: number;
  partBVolumeMl: number;
  partAMassGrams: number;
  partBMassGrams: number;
  totalMassGrams: number;
  partACost: number;
  partBCost: number;
  totalMaterialCost: number;
  batchCount: number;
  volumePerBatchMl: number;
  partAVolumePerBatchMl: number;
  partBVolumePerBatchMl: number;
  partAMassPerBatchGrams: number;
  partBMassPerBatchGrams: number;
  curedLinearScalePercent: number;
  estimatedCuredVolumeMl: number;
  assumptions: string[];
};

export function calculateMoldMix(input: MoldMixInput): MoldMixResult {
  const cavityVolume = finiteNonNegative(input.cavityVolumeMl, "cavity volume");
  const pourCount = finiteNonNegative(input.pourCount, "pour count");
  const reserve = finiteNonNegative(input.transferReserveMl, "transfer reserve");
  const wastePercent = finiteNonNegative(input.wastePercent, "waste percent");
  const ratioA = finiteNonNegative(input.partARatio, "Part A ratio");
  const ratioB = finiteNonNegative(input.partBRatio, "Part B ratio");
  const densityA = finiteNonNegative(input.partADensityGramsPerMl, "Part A density");
  const densityB = finiteNonNegative(input.partBDensityGramsPerMl, "Part B density");
  const priceA = finiteNonNegative(input.partAPricePerKg, "Part A price");
  const priceB = finiteNonNegative(input.partBPricePerKg, "Part B price");
  const maximumBatch = finiteNonNegative(input.maximumBatchVolumeMl, "maximum batch volume");
  const shrink = finiteNonNegative(input.linearShrinkPercent, "linear shrink percent");
  if (cavityVolume === 0) throw new RangeError("Cavity volume must be greater than zero.");
  if (!Number.isInteger(pourCount) || pourCount < 1 || pourCount > 10_000) throw new RangeError("Pour count must be a whole number from 1 to 10,000.");
  if (wastePercent > 100) throw new RangeError("Waste percent cannot exceed 100.");
  if (ratioA === 0 || ratioB === 0) throw new RangeError("Both mix-ratio parts must be greater than zero.");
  if (densityA === 0 || densityB === 0) throw new RangeError("Both component densities must be greater than zero.");
  if (maximumBatch === 0) throw new RangeError("Maximum batch volume must be greater than zero.");
  if (shrink > 25) throw new RangeError("Linear shrink percent cannot exceed the provisional 25% worksheet limit.");
  if (input.ratioBasis !== "mass" && input.ratioBasis !== "volume") throw new RangeError("Ratio basis must be mass or volume.");

  const targetCavityVolumeMl = cavityVolume * pourCount;
  const workingVolumeMl = targetCavityVolumeMl + reserve;
  const wasteVolumeMl = workingVolumeMl * wastePercent / 100;
  const totalMixVolumeMl = workingVolumeMl + wasteVolumeMl;
  if (!Number.isFinite(totalMixVolumeMl) || totalMixVolumeMl > 10_000_000) throw new RangeError("Total mix volume exceeds the provisional 10,000-liter worksheet limit.");

  let partAVolumeMl: number, partBVolumeMl: number, partAMassGrams: number, partBMassGrams: number;
  if (input.ratioBasis === "volume") {
    const ratioTotal = ratioA + ratioB;
    partAVolumeMl = totalMixVolumeMl * ratioA / ratioTotal;
    partBVolumeMl = totalMixVolumeMl * ratioB / ratioTotal;
    partAMassGrams = partAVolumeMl * densityA;
    partBMassGrams = partBVolumeMl * densityB;
  } else {
    const massScale = totalMixVolumeMl / (ratioA / densityA + ratioB / densityB);
    partAMassGrams = massScale * ratioA;
    partBMassGrams = massScale * ratioB;
    partAVolumeMl = partAMassGrams / densityA;
    partBVolumeMl = partBMassGrams / densityB;
  }
  const totalMassGrams = partAMassGrams + partBMassGrams;
  const partACost = partAMassGrams / 1000 * priceA, partBCost = partBMassGrams / 1000 * priceB;
  const batchCount = Math.max(1, Math.ceil(totalMixVolumeMl / maximumBatch));
  const volumePerBatchMl = totalMixVolumeMl / batchCount;
  const linearScale = 1 - shrink / 100;
  return {
    targetCavityVolumeMl,
    workingVolumeMl,
    wasteVolumeMl,
    totalMixVolumeMl,
    partAVolumeMl,
    partBVolumeMl,
    partAMassGrams,
    partBMassGrams,
    totalMassGrams,
    partACost,
    partBCost,
    totalMaterialCost: partACost + partBCost,
    batchCount,
    volumePerBatchMl,
    partAVolumePerBatchMl: partAVolumeMl / batchCount,
    partBVolumePerBatchMl: partBVolumeMl / batchCount,
    partAMassPerBatchGrams: partAMassGrams / batchCount,
    partBMassPerBatchGrams: partBMassGrams / batchCount,
    curedLinearScalePercent: linearScale * 100,
    estimatedCuredVolumeMl: targetCavityVolumeMl * linearScale ** 3,
    assumptions: [
      "Entered cavity volume and pour count are authoritative; trapped air, sprues, vents, cups, tools, and unentered plumbing are excluded.",
      `The A:B ratio is interpreted by ${input.ratioBasis}; component densities convert between volume and mass.`,
      "Waste is applied after the fixed transfer reserve, and equal-sized batches are a planning convenience rather than a cure-kinetics recommendation.",
      "Shrink is treated as uniform linear shrink; the cured-volume estimate cubes that scale and is not a substitute for the material supplier's data or a measured test cast.",
      "Cost includes entered component material prices only; taxes, shipping, pigments, fillers, release agent, consumables, labor, equipment, and failed casts are excluded."
    ]
  };
}
